import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Attempts to copy text to clipboard using modern API with fallback for older browsers
 * @param text The text to copy to clipboard
 * @returns Promise<boolean> indicating whether the operation was successful
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    // Try the modern clipboard API first
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers and some mobile devices
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (err) {
      console.error('Failed to copy text: ', err);
      return false;
    }
  }
};

/**
 * Attempts to read text from clipboard using modern API with fallback for older browsers
 * @returns Promise<string | null> - The clipboard text content or null if failed
 */
export const pasteFromClipboard = async (): Promise<string | null> => {
  try {
    // Try the modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.readText) {
      const text = await navigator.clipboard.readText();
      return text || null;
    }
  } catch (err) {
    // Modern clipboard API failed, try fallback
    console.warn('Modern clipboard API failed, attempting fallback: ', err);
  }

  // Fallback for older browsers and some mobile devices
  try {
    // Create a temporary textarea element for the fallback
    const textArea = document.createElement('textarea');
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.readOnly = false;

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    // Try to paste using execCommand
    const success = document.execCommand('paste');
    const result = success ? textArea.value : null;

    document.body.removeChild(textArea);
    return result;
  } catch (err) {
    console.error('Failed to paste from clipboard: ', err);
    return null;
  }
};

export const hasAnyValue = (obj: Record<string, any>) =>
  Object.values(obj).some(val => val !== undefined);

export function generateUUID(): string {
  // Use crypto.randomUUID() if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 generation for iOS compatibility
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}